import { useFlag } from '@calendly/feature-check-react'
import { createContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useRightRail } from 'features/right_rail'
import { timezoneSelector } from 'selectors/current_user'

import { useGetMeetingQuery } from '../api'
import { useGetMeetingQuery as useGetMeetingFromSearchQuery } from '../graphql_api'
import { mapNodeToMeeting } from '../utils'

const useGetMeeting = (meetingId) => {
  const timezone = useSelector(timezoneSelector)
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const { data: searchData, isFetching: isSearchFetching, isError: isSearchError } = useGetMeetingFromSearchQuery(
    { uuid: meetingId },
    { skip: !meetingId }
  )

  const { data, isFetching, isError } = useGetMeetingQuery({
    eventId: meetingId,
    selectedView: 'scheduled',
    scopeType: 'all_users_and_teams',
  }, { skip: !meetingId || searchData?.kind !== 'CALENDLY' })

  const meeting = useMemo(() => {
    if (!searchData) return null

    if (searchData?.kind === 'CALENDLY') return data

    return mapNodeToMeeting(searchData, timezone, hasMsTeamsFlag)
  }, [searchData, data, timezone, hasMsTeamsFlag])

  return {
    meeting,
    isFetching: isSearchFetching || isFetching,
    isError: isSearchError || isError,
  }
}

export const SearchMeetingDetailContext = createContext({})

export const SearchMeetingProvider = ({ selectedMeetingId, children }) => {
  const { meeting, isFetching, isError } = useGetMeeting(selectedMeetingId)
  const { close } = useRightRail()

  const context = useMemo(() => ({
    selectedEvent: meeting,
    isLoading: isFetching,
    handleClose: close,
    isError
  }), [meeting, isFetching, isError, close])

  return (
    <SearchMeetingDetailContext.Provider value={context}>
      {children}
    </SearchMeetingDetailContext.Provider>
  )
}
